"use client";

import { lazy, Suspense, useEffect, useState, type ComponentType, type ReactNode } from "react";
import { PaneLoading } from "@/components/workspace/PaneLoading";

type Loader = () => Promise<{ default: ComponentType<any> } | ComponentType<any>>;

type DynamicOptions = {
  ssr?: boolean;
  loading?: () => ReactNode;
};

/** A hung `import()` used to leave Suspense on its fallback forever
 *  ("Loading watchlist…", empty Advanced Chart). Show Reload after this. */
export const DYNAMIC_LOAD_TIMEOUT_MS = 12_000;

function LoadFallback({ node }: { node: ReactNode }) {
  const [late, setLate] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setLate(true), DYNAMIC_LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, []);
  if (late) {
    return (
      <div className="flex h-full min-h-[6rem] flex-col items-center justify-center gap-2 px-3 text-center">
        <p className="text-xs text-ink-muted">This pane did not load.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md px-2.5 py-1 text-[11px] font-semibold text-accent ring-1 ring-line hover:bg-accent-soft"
        >
          Reload
        </button>
      </div>
    );
  }
  return <>{node ?? <PaneLoading />}</>;
}

/**
 * Drop-in for `next/dynamic`. Vite code-splits on the dynamic `import()`;
 * `ssr: false` is a no-op in this SPA (there is no SSR).
 */
export default function dynamic(
  loader: Loader,
  opts: DynamicOptions = {},
): ComponentType<any> {
  const Lazy = lazy(async () => {
    const mod = await loader();
    if (typeof mod === "function") return { default: mod };
    if (mod && typeof mod === "object" && "default" in mod) {
      const d = (mod as { default: unknown }).default;
      if (typeof d === "function") return { default: d as ComponentType<any> };
      return mod as { default: ComponentType<any> };
    }
    throw new Error("dynamic(): loader did not resolve to a component");
  });
  const fallback = opts.loading ? opts.loading() : <PaneLoading />;
  function Dynamic(props: any) {
    return (
      <Suspense fallback={<LoadFallback node={fallback} />}>
        <Lazy {...props} />
      </Suspense>
    );
  }
  return Dynamic;
}
